import { computed, ref, toValue, watch } from "vue";
import type { MaybeRefOrGetter } from "vue";
import { useStore } from "vuex";
import DOMUtils from "@utils/dom.utils";
import DeviceUtils from "@utils/device.utils";
import { isEmpty } from "@utils/var.check";
import appConfigs from "@configs/app";
import { DRAWER_NAME } from "@modules/app/service/app.constant";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface QuestionForDnd {
  uuid?: string;
  fileName?: string;
  optionOrientation?: string;
  [key: string]: unknown;
}

export interface D3Api {
  select: (el: unknown) => D3Selection;
  zoom: (config: Record<string, unknown>, handler: (...args: unknown[]) => void) => unknown;
  zoomTransform: (el: unknown) => { x: number; y: number; k: number };
  scaleTo: (zoom: unknown, selection: unknown, scale: number) => void;
  reset: (zoom: unknown, selection: unknown) => void;
}

interface D3Selection {
  attr: (name: string, value?: unknown) => D3Selection;
  select: (selector: string) => D3Selection;
  insert: (type: string, before?: string) => D3Selection;
  nodes: () => { style: { transform: string } }[];
  call: (zoom: unknown) => D3Selection;
  on: (event: string, handler: unknown) => D3Selection;
}

export interface UseQuestionDndOptions {
  /** The d3 utility instance (legacy this.$d3). */
  d3: MaybeRefOrGetter<D3Api>;
  /** The component root element (legacy this.$el). */
  el: MaybeRefOrGetter<Element | undefined>;
  /** Vue nextTick promise (legacy this.$nextTick). */
  nextTick: MaybeRefOrGetter<Promise<void>>;
  /** The question object. */
  question?: MaybeRefOrGetter<QuestionForDnd | null | undefined>;
}

// ---------------------------------------------------------------------------
// Composable
// ---------------------------------------------------------------------------

export function useQuestionDnd(options: UseQuestionDndOptions) {
  const store = useStore();

  // --- helpers to unwrap option refs ----------------------------------
  function getD3(): D3Api {
    return toValue(options.d3);
  }

  function getEl(): Element | undefined {
    return toValue(options.el);
  }

  function getNextTick(): Promise<void> {
    return toValue(options.nextTick);
  }

  function getQuestion(): QuestionForDnd | null | undefined {
    return toValue(options.question);
  }

  // --- data (ref) ----------------------------------------------------
  const bgImageRatio = ref(1);
  const minScale = ref(1);
  const maxScale = ref(2);
  const bgImageScale = ref(1);
  const scaleUpFactor = ref(1.1);
  const scaleDownFactor = ref(0.9);
  const indicatorHidden = ref(false);
  const translateExtent = ref<number[][]>([]);
  const zoomExtent = ref<number[][]>([]);
  const bgImageOriginalWidth = ref(1);
  const bgImageOriginalHeight = ref(1);
  const bgImageScaledWidth = ref(1);
  const bgImageScaledHeight = ref(1);
  const bgImageOriginalRatio = ref(1);
  const scaledByHeight = ref(false);
  const drawerName = ref(DRAWER_NAME.SETTINGS);
  const d3ImageWrapper = ref<D3Selection | null>(null);

  // --- computed (Vuex getters) ----------------------------------------
  const getPageDrawerExpanded = computed(() => store.getters["app/getPageDrawerExpanded"]);

  const isPageDrawerExpanded = computed<boolean>(() => {
    return getPageDrawerExpanded.value(drawerName.value);
  });

  const indicatorClasses = computed(() => {
    return {
      hidden: indicatorHidden.value,
    };
  });

  const bgImageSrc = computed<string>(() => {
    const question = getQuestion();
    return question
      ? `${appConfigs.s3ResourceDomain}/question/${question.uuid}/${question.fileName}`
      : "";
  });

  const optionOrientations = computed(() => {
    const question = getQuestion();
    return question ? JSON.parse(question.optionOrientation as string) : [];
  });

  // --- methods --------------------------------------------------------

  function scaleBgImageByWidth(imageWrapper: unknown, width: number): void {
    const bgImageView = DOMUtils.parent(imageWrapper as Element, ".bg-image-view");
    bgImageOriginalRatio.value = Math.min(bgImageView.clientWidth, width) / width;
    bgImageRatio.value = bgImageOriginalRatio.value;
  }

  function updateBgImageRatio(width: number, height: number, imageWrapper: unknown): void {
    const bgImageMaxHeight = 0.75 * window.innerHeight;
    const bgImageView = DOMUtils.parent(imageWrapper as Element, ".bg-image-view");

    if (height > bgImageMaxHeight) {
      const heightRatio = height / bgImageMaxHeight;
      const widthRatio = width / bgImageView.clientWidth;

      if (heightRatio > widthRatio) {
        bgImageOriginalRatio.value = bgImageMaxHeight / height;
        bgImageRatio.value = bgImageOriginalRatio.value;
        scaledByHeight.value = true;
      } else {
        scaleBgImageByWidth(imageWrapper, width);
      }
    } else {
      scaleBgImageByWidth(imageWrapper, width);
    }

    bgImageScaledWidth.value = width * bgImageRatio.value;
    bgImageScaledHeight.value = height * bgImageRatio.value;
  }

  function handleScrollZoom(imageWrapper: unknown): void {
    const $d3 = getD3();
    const d3Transform = $d3.zoomTransform(imageWrapper);
    const group = $d3.select(imageWrapper).select("g").nodes()[0];

    const x = d3Transform.x;
    const y = d3Transform.y;
    const k = d3Transform.k;

    bgImageScale.value = k;
    group.style.transform = `translate(${x}px, ${y}px) scale(${k})`;
  }

  function getD3Zoom(imageWrapper: unknown): unknown {
    const $d3 = getD3();
    return $d3.zoom(
      {
        scaleExtent: [minScale.value, maxScale.value],
        translateExtent: translateExtent.value,
        zoomExtent: zoomExtent.value,
      },
      handleScrollZoom.bind(null, imageWrapper),
    );
  }

  function bindMouseZoom(imageWrapper: unknown): void {
    const d3Zoom = getD3Zoom(imageWrapper);
    d3ImageWrapper.value!.call(d3Zoom);
    d3ImageWrapper.value!.on("dblclick.zoom", handleDoubleClick.bind(null, d3Zoom, imageWrapper));
  }

  function unbindMouseZoom(): void {
    d3ImageWrapper.value!.on("wheel.zoom", null);
    d3ImageWrapper.value!.on("mousedown.zoom", null);
    d3ImageWrapper.value!.on("dblclick.zoom", null);
  }

  function setTranslateAndZoomExtent(imageWrapper: Element): void {
    const bgImageView = DOMUtils.parent(imageWrapper, ".bg-image-view") as Element;
    const bgImage = DOMUtils.elOf(imageWrapper, ".bg-image")[0] as SVGImageElement;

    const imageWrapperWidth = imageWrapper.getBoundingClientRect().width;
    const imageWrapperHeight = imageWrapper.getBoundingClientRect().height;

    const bgImageWidth = bgImage.width.baseVal.value;
    const bgImageHeight = bgImage.height.baseVal.value;

    const bgImageViewWidth = bgImageView.getBoundingClientRect().width;

    translateExtent.value = [
      [0, 0],
      [bgImageWidth, bgImageHeight],
    ];

    let diffOfBgImageViewAndWrapperHalved = 0;

    if (bgImageViewWidth !== imageWrapperWidth) {
      diffOfBgImageViewAndWrapperHalved = (bgImageViewWidth - imageWrapperWidth) / 2;
    }

    zoomExtent.value = [
      [0 - diffOfBgImageViewAndWrapperHalved, 0],
      [imageWrapperWidth + diffOfBgImageViewAndWrapperHalved, imageWrapperHeight],
    ];
  }

  function handleZoom(isZoomIn: boolean, d3Zoom: unknown, imageWrapper: unknown): void {
    const $d3 = getD3();
    const d3Transform = $d3.zoomTransform(imageWrapper);
    const d3Wrapper = $d3.select(imageWrapper);

    const currentScaleValue = d3Transform.k;
    if (
      (isZoomIn && currentScaleValue >= maxScale.value) ||
      (!isZoomIn && currentScaleValue <= minScale.value)
    ) {
      return;
    }

    const scaleFactor = isZoomIn ? scaleUpFactor.value : scaleDownFactor.value;
    const newScaleValue = currentScaleValue * scaleFactor;
    bgImageScale.value = newScaleValue > maxScale.value ? maxScale.value : newScaleValue;
    $d3.scaleTo(d3Zoom, d3Wrapper, bgImageScale.value);
  }

  function handleZoomReset(d3Zoom: unknown, imageWrapper: unknown): void {
    const $d3 = getD3();
    const d3Wrapper = $d3.select(imageWrapper);
    $d3.reset(d3Zoom, d3Wrapper);
  }

  function handleDoubleClick(d3Zoom: unknown, imageWrapper: unknown): void {
    const $d3 = getD3();
    const d3Transform = $d3.zoomTransform(imageWrapper);
    const d3Wrapper = $d3.select(imageWrapper);

    const currentScale = d3Transform.k;

    if (currentScale === maxScale.value) {
      $d3.reset(d3Zoom, d3Wrapper);
    } else {
      $d3.scaleTo(d3Zoom, d3Wrapper, maxScale.value);
    }
  }

  function initBgImageToolBar(d3Zoom: unknown, imageWrapper: unknown): void {
    const bgImageWrapperEl = DOMUtils.parent(imageWrapper as Element, ".bg-image-wrapper");
    const imageToolbar = DOMUtils.elOf(bgImageWrapperEl, ".image-toolbar")[0];

    if (isEmpty(imageToolbar)) return;

    const zoomInBtn = DOMUtils.elOf(imageToolbar as Element, ".zoom-in")[0];
    if (!isEmpty(zoomInBtn)) {
      (zoomInBtn as HTMLElement).addEventListener("click", (event: Event) => {
        event.stopPropagation();
        handleZoom(true, d3Zoom, imageWrapper);
      });
    }

    const zoomOutBtn = DOMUtils.elOf(imageToolbar as Element, ".zoom-out")[0];
    if (!isEmpty(zoomOutBtn)) {
      (zoomOutBtn as HTMLElement).addEventListener("click", (event: Event) => {
        event.stopPropagation();
        handleZoom(false, d3Zoom, imageWrapper);
      });
    }

    const zoomResetBtn = DOMUtils.elOf(imageToolbar as Element, ".zoom-reset")[0];
    if (!isEmpty(zoomResetBtn)) {
      (zoomResetBtn as HTMLElement).addEventListener("click", (event: Event) => {
        event.stopPropagation();
        handleZoomReset(d3Zoom, imageWrapper);
      });
    }
  }

  function initd3Zoom(imageWrapper: unknown): void {
    setTimeout(() => {
      const $d3 = getD3();
      d3ImageWrapper.value = $d3.select(imageWrapper);

      setTranslateAndZoomExtent(imageWrapper as Element);
      const d3Zoom = getD3Zoom(imageWrapper);
      bindMouseZoom(imageWrapper);
      initBgImageToolBar(d3Zoom, imageWrapper);
      $d3.reset(d3Zoom, d3ImageWrapper.value);
    }, 500);
  }

  function initBackgroundImage(
    width: number,
    height: number,
    imageWrapper: unknown,
    imageSrc: string | null = null,
  ): void {
    const comfortReadingWidth = 680;
    const $d3 = getD3();
    const d3imageWrapper = $d3.select(imageWrapper);
    const questionView = DOMUtils.parent(imageWrapper as Element, ".question-view");

    bgImageOriginalWidth.value = width;
    bgImageOriginalHeight.value = height;

    if (width > comfortReadingWidth) {
      if (questionView) {
        DOMUtils.addClass(questionView, "image-rescaled");
      }
    } else {
      if (questionView) {
        DOMUtils.removeClass(questionView, "image-rescaled");
      }
    }

    updateBgImageRatio(width, height, imageWrapper);

    d3imageWrapper
      .attr("width", bgImageScaledWidth.value)
      .attr("height", bgImageScaledHeight.value);

    const group = d3imageWrapper.select("g");
    group
      .insert("svg:image", ":first-child")
      .attr("xlink:href", imageSrc ?? bgImageSrc.value)
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", bgImageScaledWidth.value)
      .attr("height", bgImageScaledHeight.value)
      .attr("class", "bg-image");

    initd3Zoom(imageWrapper);
  }

  async function handleIndicatorClick(event: { clientX: number; clientY: number }): Promise<void> {
    const { clientX, clientY } = event;

    const mouseEvent = new MouseEvent("click", {
      view: window,
      bubbles: true,
      cancelable: true,
      clientX: clientX,
      clientY: clientY,
    });
    indicatorHidden.value = true;

    await getNextTick();
    const elementFromPoint = document.elementFromPoint(clientX, clientY);
    elementFromPoint!.dispatchEvent(mouseEvent);
  }

  function transformBgImage(): void {
    const el = getEl();
    const imageWrapper = DOMUtils.elOf(el!, ".image-wrapper")[0];
    const bgImageView = DOMUtils.parent(imageWrapper as Element, ".bg-image-view");

    let transformedWidth = bgImageOriginalWidth.value;
    let transformedHeight = bgImageOriginalHeight.value;

    setTimeout(() => {
      if (isPageDrawerExpanded.value) {
        bgImageRatio.value =
          Math.min(imageWrapper.clientWidth, bgImageOriginalWidth.value) /
          bgImageOriginalWidth.value;

        transformedWidth *= bgImageRatio.value;
        transformedHeight *= bgImageRatio.value;
      } else if (scaledByHeight.value) {
        transformedWidth = bgImageScaledWidth.value;
        transformedHeight = bgImageScaledHeight.value;
      } else if (transformedWidth > bgImageView.clientWidth) {
        if (transformedWidth * bgImageOriginalRatio.value < bgImageView.clientWidth) {
          updateBgImageRatio(bgImageOriginalWidth.value, bgImageOriginalHeight.value, imageWrapper);
          transformedWidth *= bgImageRatio.value;
          transformedHeight *= bgImageRatio.value;
        } else {
          bgImageRatio.value = bgImageOriginalRatio.value;
          transformedWidth = bgImageScaledWidth.value;
          transformedHeight = bgImageScaledHeight.value;
        }
      }

      const $d3 = getD3();
      const d3imageWrapperSel = $d3.select(imageWrapper);
      const d3bgImg = d3imageWrapperSel.select(".bg-image");
      d3imageWrapperSel.attr("width", transformedWidth).attr("height", transformedHeight);
      d3bgImg.attr("width", transformedWidth).attr("height", transformedHeight);

      initd3Zoom(imageWrapper);
    }, 300);
  }

  // --- watcher --------------------------------------------------------
  watch(isPageDrawerExpanded, (drawerExpanded: boolean) => {
    const el = getEl();
    const bgImage = DOMUtils.elOf(el!, ".image-wrapper .bg-image")[0];

    if (!bgImage) {
      return false;
    }

    if (DeviceUtils.isDekstop() || DeviceUtils.isDeviceInLandscape()) {
      if (scaledByHeight.value) {
        bgImageRatio.value = bgImageOriginalRatio.value;
      } else {
        bgImageRatio.value = drawerExpanded ? bgImageOriginalRatio.value : 1;
      }

      transformBgImage();
    }
  });

  // --- public surface -------------------------------------------------
  return {
    // data (refs)
    bgImageRatio,
    minScale,
    maxScale,
    bgImageScale,
    scaleUpFactor,
    scaleDownFactor,
    indicatorHidden,
    translateExtent,
    zoomExtent,
    bgImageOriginalWidth,
    bgImageOriginalHeight,
    bgImageScaledWidth,
    bgImageScaledHeight,
    bgImageOriginalRatio,
    scaledByHeight,
    drawerName,
    d3ImageWrapper,

    // computed
    isPageDrawerExpanded,
    indicatorClasses,
    bgImageSrc,
    optionOrientations,

    // methods
    initBackgroundImage,
    updateBgImageRatio,
    scaleBgImageByWidth,
    initd3Zoom,
    getD3Zoom,
    bindMouseZoom,
    unbindMouseZoom,
    setTranslateAndZoomExtent,
    initBgImageToolBar,
    handleScrollZoom,
    handleZoom,
    handleZoomReset,
    handleDoubleClick,
    handleIndicatorClick,
    transformBgImage,
  };
}
